import type { Ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import { notify } from '@/services/notice';
import type { ModuleRecord } from '@/types/domain';
import { errorMessage } from '@/utils/format';
import type { InlineField } from '@/utils/issue-status';

/**
 * 问题清单单元格就地改的写入路径，项目页和全局页共用一份。
 *
 * 抽出来是因为这套东西原先只长在项目页上：全局问题清单（/modules/issues）
 * 复用的是同一个 ProjectIssueList，格子照样点得开、改得动，
 * 但那个页面原先没接 inline-update 事件——组件 emit 出来没人收，
 * 既不写库也不报错，界面上却像是存上了。
 *
 * 静默丢数据比报错难发现得多（改完刷新才知道白改），所以两边不能各写各的：
 * 尤其是「进展」那条追加口径，一旦一边写成覆盖，同一条问题的处理日志
 * 会因为人在哪个页面改而长成两个样。
 */
export interface IssueInlineUpdate {
  item: ModuleRecord;
  field: InlineField;
  value: string;
}

/**
 * 进展前缀用的「今天」，锁死东八区。
 *
 * 原来是 `new Date().toISOString().slice(5, 10)`，那是格林尼治的日期：
 * 北京时间早上 8 点前写的进展会盖上前一天的戳，而车间早班正好从 8 点开始，
 * 交接班第一条进展十有八九落在这个窗口里。
 *
 * 也不用浏览器本地时区——库的 timezone 就是 Asia/Shanghai，全站写日期都显式带 +08:00，
 * 这里跟着同一个口径，出差改问题的人才不会在日志里留下一个对不上的日期。
 * 用 formatToParts 取值是因为各 locale 的分隔符不一样（zh-CN 给的是 08/23），
 * 而库里已有的进展都是 [MM-DD]，格式不能换。
 */
function todayInShanghai(): string {
  const parts = new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const pick = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  return `${pick('month')}-${pick('day')}`;
}

/**
 * 一列改动落成一个写入体。
 *
 * 六列各自落到不同的地方：责任人是主表列，等级/分类/当前计划在 normalized 里
 * （后端更新 normalized 用的是 `||` 合并，只传一个键不会冲掉其他字段）。
 * 状态不在这里——它有流转校验，得走 advance，见下面 inlineUpdateIssue。
 *
 * 进展是**追加**不是覆盖：它是一条流水日志，列表里只露最新一条，
 * 改成覆盖的话点一次就把之前的记录全冲了。日期前缀按 [MM-DD] 补在最前面，
 * 人才看得出哪条是什么时候写的。
 */
export function issueInlineBody(
  item: ModuleRecord, field: InlineField, value: string,
): Record<string, unknown> | null {
  if (field === 'ownerName') return { ownerName: value || null };
  if (field === 'severityGrade') return { normalized: { severityGrade: value } };
  if (field === 'category') return { normalized: { category: value } };
  if (field === 'currentPlanDate') return { normalized: { currentPlanDate: value } };
  if (field === 'progress') {
    const previous = String(
      (item.normalized as Record<string, unknown> | undefined)?.progressLog ?? '',
    ).trim();
    const stamped = `[${todayInShanghai()}] ${value}`;
    return { normalized: { progressLog: previous ? `${stamped}\n${previous}` : stamped } };
  }
  return null;
}

/**
 * @param items 页面上那份清单；写成功后就地换掉这一条
 * @param reload 写失败时重新拉全表，把界面拉回库里的真实值
 */
export function useIssueInlineUpdate(
  items: Ref<ModuleRecord[]>,
  reload: () => Promise<void>,
) {
  async function inlineUpdateIssue({ item, field, value }: IssueInlineUpdate): Promise<void> {
    try {
      /*
        问题的写入统一走 module-tools 那两条路由：通用的 modules 路由只认
        「这个项目里能不能写」，认不出提出者和责任人，做不了 0823 定的分层
        （责任人能改内容、改不了状态和实际完成时间）。
      */
      let saved: ModuleRecord;
      if (field === 'status') {
        saved = await eqsApi.advanceIssueRecord(item.id, value);
      } else {
        const body = issueInlineBody(item, field, value);
        if (!body) return;
        saved = await eqsApi.updateIssueRecord(item.id, body);
      }
      // 只换这一条，不整表重载——重载会把滚动位置和筛选后的视线全丢掉
      const index = items.value.findIndex((row) => String(row.id) === String(item.id));
      if (index >= 0) items.value[index] = saved;
    } catch (caught) {
      notify('error', '这一格没存上', errorMessage(caught));
      // 存失败时把真实值拉回来，不让界面停在一个没落库的值上
      await reload();
    }
  }

  return { inlineUpdateIssue };
}
